import React from 'react';
import { HiOutlineChevronDown, HiOutlineCheck, HiOutlineX, HiOutlineSearch } from '../icons';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps {
  options: SelectOption[];
  value?: string | string[];
  onChange: (value: string | string[]) => void;
  placeholder?: string;
  label?: string;
  error?: string;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  multiple?: boolean;
  searchable?: boolean;
  clearable?: boolean;
  fullWidth?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

const Select = React.forwardRef<React.ElementRef<'div'>, SelectProps>(
  (
    {
      options,
      value,
      onChange,
      placeholder = 'Select an option',
      label,
      error,
      helperText,
      required = false,
      disabled = false,
      multiple = false,
      searchable = false,
      clearable = false,
      fullWidth = false,
      size = 'md',
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [searchQuery, setSearchQuery] = React.useState('');
    const selectRef = React.useRef<React.ElementRef<'div'>>(null);

    React.useImperativeHandle(ref, () => selectRef.current!);

    // Handle click outside
    React.useEffect(() => {
      const handleClickOutside = (event: globalThis.MouseEvent) => {
        if (selectRef.current && !selectRef.current.contains(event.target as globalThis.Node)) {
          setIsOpen(false);
          setSearchQuery('');
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Filter options based on search
    const filteredOptions = React.useMemo(() => {
      if (!searchQuery) return options;
      return options.filter(option =>
        option.label.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }, [options, searchQuery]);

    // Get selected options
    const selectedOptions = React.useMemo(() => {
      if (!value) return [];
      const values = Array.isArray(value) ? value : [value];
      return options.filter(option => values.includes(option.value));
    }, [value, options]);

    // Handle option selection
    const handleOptionSelect = (option: SelectOption) => {
      if (option.disabled) return;

      if (multiple) {
        const currentValues = Array.isArray(value) ? value : [];
        const newValues = currentValues.includes(option.value)
          ? currentValues.filter(v => v !== option.value)
          : [...currentValues, option.value];
        onChange(newValues);
      } else {
        onChange(option.value);
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    // Handle clear
    const handleClear = (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange(multiple ? [] : '');
    };

    // Handle remove single item in multiple mode
    const handleRemoveItem = (optionValue: string, e: React.MouseEvent) => {
      e.stopPropagation();
      if (multiple && Array.isArray(value)) {
        onChange(value.filter(v => v !== optionValue));
      }
    };

    const sizeClasses = {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-4 py-2 text-sm',
      lg: 'px-5 py-3 text-base',
    };

    const baseClasses =
      'relative border border-gray-300 bg-white rounded-lg cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors';
    const disabledClasses = disabled ? 'opacity-50 cursor-not-allowed' : '';
    const errorClasses = error ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : '';
    const widthClass = fullWidth ? 'w-full' : '';

    const selectClasses =
      `${baseClasses} ${sizeClasses[size]} ${disabledClasses} ${errorClasses} ${widthClass}`.trim();

    return (
      <div className={fullWidth ? 'w-full' : ''}>
        {label && (
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}

        <div ref={selectRef} className="relative">
          <div
            className={selectClasses}
            onClick={() => !disabled && setIsOpen(!isOpen)}
            role="button"
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            tabIndex={disabled ? -1 : 0}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIsOpen(!isOpen);
              }
            }}
          >
            <div className="flex items-center justify-between">
              <div className="flex-1 flex items-center flex-wrap gap-1">
                {selectedOptions.length > 0 ? (
                  multiple ? (
                    selectedOptions.map(option => (
                      <span
                        key={option.value}
                        className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800"
                      >
                        {option.label}
                        <button
                          type="button"
                          onClick={e => handleRemoveItem(option.value, e)}
                          className="ml-1 hover:text-blue-600"
                        >
                          <HiOutlineX className="h-3 w-3" />
                        </button>
                      </span>
                    ))
                  ) : (
                    <span className="text-gray-900">{selectedOptions[0]?.label}</span>
                  )
                ) : (
                  <span className="text-gray-500">{placeholder}</span>
                )}
              </div>

              <div className="flex items-center space-x-1">
                {clearable && selectedOptions.length > 0 && (
                  <button
                    type="button"
                    onClick={handleClear}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <HiOutlineX className="h-4 w-4" />
                  </button>
                )}
                <HiOutlineChevronDown
                  className={`h-4 w-4 text-gray-400 transition-transform ${
                    isOpen ? 'transform rotate-180' : ''
                  }`}
                />
              </div>
            </div>
          </div>

          {isOpen && (
            <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
              {searchable && (
                <div className="p-2 border-b border-gray-200">
                  <div className="relative">
                    <HiOutlineSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Search options..."
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                    />
                  </div>
                </div>
              )}

              <div role="listbox" aria-multiselectable={multiple}>
                {filteredOptions.length > 0 ? (
                  filteredOptions.map(option => {
                    const isSelected = selectedOptions.some(
                      selected => selected.value === option.value
                    );

                    return (
                      <div
                        key={option.value}
                        className={`px-4 py-2 cursor-pointer flex items-center justify-between hover:bg-gray-50 ${
                          option.disabled ? 'opacity-50 cursor-not-allowed' : ''
                        } ${isSelected ? 'bg-blue-50 text-blue-900' : 'text-gray-900'}`}
                        onClick={() => handleOptionSelect(option)}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleOptionSelect(option);
                          }
                        }}
                        role="option"
                        aria-selected={isSelected}
                        tabIndex={0}
                      >
                        <span>{option.label}</span>
                        {isSelected && <HiOutlineCheck className="h-4 w-4 text-blue-600" />}
                      </div>
                    );
                  })
                ) : (
                  <div className="px-4 py-2 text-gray-500">No options found</div>
                )}
              </div>
            </div>
          )}
        </div>

        {(error || helperText) && (
          <div className="mt-1">
            {error ? (
              <span className="text-sm text-red-600">{error}</span>
            ) : (
              <span className="text-sm text-gray-500">{helperText}</span>
            )}
          </div>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';

export default Select;
